'use client';

import { Default_Image } from '@/constants';
import { ProductCarouselType } from '@/enums';
import { useElementOnScreen } from '@/hooks/intersectionObserver';
import { IconArrowLeftDouble } from '@/icons/IconArrowLeftDouble';
import { IconArrowRightDouble } from '@/icons/IconArrowRightDouble';
import { IProductSlide } from '@/interfaces/product';
import { useEffect, useState } from 'react';
import { Autoplay, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { Swiper, SwiperSlide } from 'swiper/react';
import CustomLink from '@/components/CustomLink';
import ProductCard from '@/components/ProductCard';
import { SliderArrow } from '@/components/SliderArrow';

const SpecificProductSlider = ({
  title,
  products,
  className = '',
  slideClassName = 'sm:pr-1',
  config = { slidesPerView: 'auto', spaceBetween: 14 },
  seName,
  carouselType,
  autoPlayTimer,
  dataTestId = 'specific-product-slider',
}: {
  title?: string;
  products: IProductSlide[];
  className?: string;
  slideClassName?: string;
  config?: any;
  carouselType?: string;
  seName?: string;
  autoPlayTimer: number;
  dataTestId?: string;
}) => {
  const [isMounted, setIsMounted] = useState<boolean>(false);

  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    rootMargin: '-100px',
    threshold: 0.0,
  });

  useEffect(() => {
    if (isVisible) {
      setIsMounted(true);
      containerRef?.current?.swiper?.autoplay.start();
    } else {
      containerRef?.current?.swiper?.autoplay?.stop();
    }
  }, [isVisible, containerRef]);

  const swiperInternalComponent = (items?: IProductSlide[]) => {
    return (
      <Swiper
        {...config}
        key={isMounted.toString()}
        modules={[Navigation, Autoplay]}
        ref={containerRef}
        slidesPerView={1}
        autoplay={{
          delay: autoPlayTimer - 1000,
        }}
        breakpoints={{
          400: {
            slidesPerView: 2,
            autoplay: {
              delay: autoPlayTimer - 1000,
            },
          },
          640: {
            slidesPerView: 3,
            autoplay: {
              delay: autoPlayTimer,
            },
          },
          1024: {
            slidesPerView: 4,
            autoplay: {
              delay: autoPlayTimer,
            },
          },
          1400: {
            slidesPerView: 5,
            autoplay: {
              delay: autoPlayTimer,
            },
          },
          1700: {
            slidesPerView: 6,
            autoplay: {
              delay: autoPlayTimer,
            },
          },
        }}
        navigation={{
          prevEl: '.products-swipe-prev',
          nextEl: '.products-swipe-next',
        }}
        className="content-slider"
        wrapperClass="flex"
        data-testid={`${dataTestId}-swiper`}
      >
        {items?.map((slide, index) => (
          <SwiperSlide key={slide.id} style={{ width: 'fit-content' }} data-testid={`${dataTestId}-slide-${index}`}>
            <ProductCard
              key={slide.seName}
              className={slideClassName}
              name={slide?.name}
              url={`/product/${slide.seName}`}
              category={slide?.CategoryName}
              options={slide?.options}
              imageUrl={slide?.FeaturedImageUrl !== '' ? slide.FeaturedImageUrl : Default_Image}
              imageAlt={`Slide ${index} ${slide?.altText ? slide?.altText : slide?.seName}`}
              oldPrice={slide?.productPrice?.OldPrice}
              price={slide?.productPrice?.Price}
              seName={slide?.seName}
            />
          </SwiperSlide>
        ))}
        <SwiperSlide key="view-all" className="" style={{ width: 'fit-content', height: 'auto' }} data-testid={`${dataTestId}-view-all-slide`}>
          <div className={`${slideClassName} mb-5 flex h-full items-center justify-center`} data-testid={`${dataTestId}-view-all-container`}>
            {carouselType === ProductCarouselType.Category && (
              <CustomLink
                href={`/category/${seName}`}
                className="underline-custom mt-1 block text-lg font-semibold uppercase tracking-widest"
                data-testid={`${dataTestId}-view-all-category-link`}
              >
                View All
              </CustomLink>
            )}
            {carouselType === ProductCarouselType.Brand && (
              <CustomLink
                href={`/brand/${seName}`}
                className="underline-custom mt-1 block text-lg font-semibold uppercase tracking-widest"
                data-testid={`${dataTestId}-view-all-brand-link`}
              >
                View All
              </CustomLink>
            )}
          </div>
        </SwiperSlide>
      </Swiper>
    );
  };

  return (
    <div className="mx-12" data-testid={dataTestId}>
      <p className="product-carousel__heading" data-testid={`${dataTestId}-heading`}>
        <span className="product-carousel__title" data-testid={`${dataTestId}-title`}>
          {title}
        </span>
      </p>
      <div className={`product-slider relative w-full px-4 lg:px-10 after:${className}`} data-testid={`${dataTestId}-slider-container`}>
        {isMounted ? swiperInternalComponent(products) : swiperInternalComponent(products && products?.length > 6 ? products?.slice(0, 6) : products)}

        <div className="products-swipe-prev swipe-prev absolute bottom-0 left-0 right-0 top-0" data-testid={`${dataTestId}-prev-arrow`}>
          <SliderArrow className="left-0 -translate-x-full" data-testid={`${dataTestId}-prev-arrow-button`}>
            <IconArrowLeftDouble className="h-8 w-8 text-gray-600" />
          </SliderArrow>
        </div>
        <div className="products-swipe-next swipe-next absolute bottom-0 left-0 right-0 top-0" data-testid={`${dataTestId}-next-arrow`}>
          <SliderArrow className="right-0 translate-x-full" data-testid={`${dataTestId}-next-arrow-button`}>
            <IconArrowRightDouble className="h-8 w-8 text-gray-600" />
          </SliderArrow>
        </div>
      </div>
      <div className="-mx-12 border border-t-0 border-black pt-8" data-testid={`${dataTestId}-border`}></div>
      <style jsx>{`
        .product-carousel__heading {
          position: relative;
          margin: 0 0 10px;
          line-height: 23px;
          text-align: center;
        }
        .product-carousel__heading:before {
          position: absolute;
          margin-left: -3rem;
          margin-right: -3rem;
          top: 14px;
          right: 0;
          left: 0;
          height: 1px;
          content: '';
          background: #000;
        }
        .product-carousel__heading:after {
          position: absolute;
          top: 14px;
          right: 0;
          left: 0;
          height: 1px;
          content: '';
          padding-top: 24px;
          border-left: 1px solid black;
          border-right: 1px solid black;
          margin-left: -3rem;
          margin-right: -3rem;
        }
        .product-carousel__title {
          letter-spacing: 0.2em;
          text-transform: uppercase;
          position: relative;
          display: inline-block;
          padding: 0 20px;
          font-size: 16px;
          line-height: 28px;
          background: #fff;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default SpecificProductSlider;
